import { useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { MessageContext } from '@/helpers/MessageProvider';
import useMavLinkCommandHandler from '@/hooks/useMavLinkCommandHandler';
import useMavLinkTelemetryHandler from '@/hooks/useMavLinkTelemetryHandler';
import usePingListener from '@/hooks/usePingListener';
import useWaitingRobot from '@/hooks/useWaitingRobot';
import useWaitingRobotTimer from '@/hooks/useWaitingRobotTimer';

const useOrganizer = (robot) => {
  const { publishCommand } = useContext(MessageContext);
  const address = useSelector((state) => state.telemetry[robot.id]?.address);

  usePingListener(robot.id);
  useWaitingRobotTimer(robot.id);
  const isWaiting = useWaitingRobot(robot.id);

  useMavLinkTelemetryHandler(robot);
  const { addInterval, clearIntervals } = useMavLinkCommandHandler(robot);

  const [sysId, compId] = useMemo(() => {
    return [address?.sysId, address?.compId];
  }, [address]);

  useEffect(() => {
    if (!robot.isActive) return;
    if (isWaiting) return;
    if (!sysId) return;

    if (robot.isOwned) {
      publishCommand(robot, 'request/autopilot_version', [[sysId, compId]]);
    }

    // prettier-ignore
    // eslint-disable-next-line no-lone-blocks
    {
      addInterval('request/data_stream', [[sysId, compId, 1, 2, 1]], 3000); // MAV_DATA_STREAM_RAW_SENSORS
      addInterval('request/data_stream', [[sysId, compId, 2, 2, 1]], 3000); // MAV_DATA_STREAM_EXTENDED_STATUS
      addInterval('request/data_stream', [[sysId, compId, 3, 2, 1]], 3000); // MAV_DATA_STREAM_RC_CHANNELS
      addInterval('request/data_stream', [[sysId, compId, 6, 3, 1]], 3000); // MAV_DATA_STREAM_POSITION
      addInterval('request/data_stream', [[sysId, compId, 10, 5, 1]], 3000); // MAV_DATA_STREAM_EXTRA1
      addInterval('request/data_stream', [[sysId, compId, 11, 5, 1]], 3000); // MAV_DATA_STREAM_EXTRA2
      addInterval('request/data_stream', [[sysId, compId, 12, 3, 1]], 3000); // MAV_DATA_STREAM_EXTRA3
      addInterval('request/heartbeat', [[]], 1000);
      addInterval('request/message_interval', [[sysId, compId, 242, 1000000]], 11000); // HOME_POSITION
    }

    return () => {
      clearIntervals();
    };
  }, [robot.isActive, isWaiting, sysId]);
};

export default useOrganizer;
